import { MessageInput } from "@App/Components/atoms/checkout/MessageInput";
import { Field } from "@App/Components/atoms/common/Field";
import { InputHint } from "@App/Components/atoms/common/InputHint";
import { InputLabel } from "@App/Components/atoms/common/InputLabel";
import { VehicleReturnTOS } from "@App/Components/ClientSpecific/MeinAuto/VehicleReturnTOS";
import { Checkbox } from "@App/Components/common/Checkbox";
import { Input } from "@App/Components/common/Input";
import { MileageInput } from "@App/Components/common/MileageInput";
import { PhoneInput } from "@App/Components/common/PhoneInput";
import { Loader } from "@App/Components/Loader";
import { Modal } from "@App/Components/Modal";
import { AddressForm } from "@App/Components/molecules/checkout/AddressForm";
import { RentalCarSelect } from "@App/Components/molecules/whitelabel/ayvens/RentalCarSelect";
import { NumberPlateInput } from "@App/Components/NumberPlateInput";
import { GaragePropertyContext } from "@App/contexts/listing/GaragePropertyContext";
import { useCreateInquiry } from "@App/hooks/api/mutations/useCreateInquiry";
import { useCountry } from "@App/hooks/useCountry";
import { actionCreators, type State } from "@App/state";
import { selectFleetVehicleReturn } from "@App/state/selectors/fleet";
import {
  ActiveInquiryExistsForNumberplate,
  ActiveInquiryExistsForNumberplateAndService,
  CustomerAddressNotGiven,
  EarliestReturnDateNotReached,
  getEarliestReturnDate,
  MeinAutoCustomerContractNotFound,
  MeinAutoNoTyreProductInCustomerContract,
  NoDriverFoundForNumberPlate,
  NumberPlateNotFound,
  SlotAlreadyBlocked,
  SlotAlreadyBooked,
  VehicleNotFoundInExternalFleet,
} from "@App/utils/conditionals/errors";
import { GarageHasNationwideService } from "@App/utils/conditionals/garages";
import {
  OrganizationIsALD,
  OrganizationIsArval,
  OrganizationIsMARA,
  OrganizationIsMeinAuto,
  OrganizationIsMFS,
  OrganizationIsMobilityConcept,
  OrganizationIsRepareo,
} from "@App/utils/conditionals/organization";
import {
  PremiumServiceIsCollectAndReturn,
  PremiumServiceIsRentalCar,
  ServiceIsCertificate,
  ServiceIsVehicleReturn,
  ServiceIsWheelChange,
  ServiceRequiresAdditionalInformation,
} from "@App/utils/conditionals/services";
import { formatDate } from "@App/utils/dates";
import { getConfig } from "@App/utils/getConfig";
import { bindActionCreators } from "@reduxjs/toolkit";
import { VinInput } from "@App/Components/atoms/forms/VinInput";
import { parseISO } from "date-fns";
import {
  type FormEvent,
  Fragment,
  type FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

type ContactFormProps = {
  onSuccessful: () => void;
};

export const ContactForm: FunctionComponent<ContactFormProps> = ({
  onSuccessful,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const { availableGarageProperties } = useContext(GaragePropertyContext);

  const organization = useSelector((state: State) => state.orders.organization);

  const firstName = useSelector((state: State) => state.orders.firstName);
  const lastName = useSelector((state: State) => state.orders.lastName);
  const email = useSelector((state: State) => state.orders.email);
  const phone = useSelector((state: State) => state.orders.phone);
  const message = useSelector((state: State) => state.orders.message);
  const vin = useSelector((state: State) => state.orders.vin);
  const claimNumber = useSelector((state: State) => state.orders.claimNumber);
  const contractNumber = useSelector((state: State) => state.orders.contractNumber);
  const fleetVehicleReturn = useSelector(selectFleetVehicleReturn);
  const reviewOptIn = useSelector((state: State) => state.orders.reviewOptIn);

  const termsLink = "https://www.repareo.de/allgemeine-nutzungsbedingungen-fuer-repareo/";
  const ayvensPrivacyLink = "https://www.ayvens.com/de-de/datenschutz/";

  const dispatch = useDispatch();
  const {
    setFirstName,
    setLastName,
    setVIN,
    setClaimNumber,
    setEmail,
    setPhone,
    setMileage,
    setNumberPlate,
    setMessage,
    setStreet,
    setCity,
    setPostcode,
    setAddition,
    setContractNumber,
    setReviewOptIn,
  } = bindActionCreators(actionCreators, dispatch);

  const { street, city, postcode, addition, userConfirmedAddress } = useSelector(
    (state: State) => state.orders.address,
  );
  const [numberPlateChecking, setNumberPlateChecking] = useState(false);
  const [vinHint, setVinHint] = useState<boolean>(false);
  const [tooltipShowing, setTooltipShowing] = useState(false);
  const [addressRequired, setAddressRequired] = useState(false);
  const [addressFormDisabled, setAddressFormDisabled] = useState(false);
  const [tos, setTos] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [adacMember, setAdacMember] = useState(false);
  const [additionalCosts, setAdditionalCosts] = useState(false);
  const numberPlate = useSelector((state: State) => state.orders.numberPlate);

  const countryCode = useCountry();

  const mileage = useSelector((state: State) => state.orders.mileage);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);

  const [showNumberPlateDialog, setShowNumberPlateDialog] = useState(false);

  useEffect(() => {
    if (
      premiumServices?.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService))
    ) {
      setAddressRequired(true);
    }
  }, [premiumServices]);

  useEffect(() => {
    setAddressFormDisabled(false);
    if (userConfirmedAddress && addressRequired) setAddressFormDisabled(true);
  }, [userConfirmedAddress, addressRequired]);

  const {
    service,
    garage,
    slot,
    error,
    loading,
    inquiry,
    validationErrors,
    checkNumberPlate,
    submit,
  } = useCreateInquiry({
    firstName,
    lastName,
    email,
    phone,
    tos,
    marketing,
    adacMember,
    street,
    city,
    postcode,
    addition,
    message,
    numberPlate,
    mileage,
    availableGarageProperties,
    additionalCosts,
    reviewOptIn,
  });

  useEffect(() => {
    if (inquiry && inquiry.id) {
      onSuccessful && onSuccessful();
    }
  }, [inquiry]);

  const getPlaceholder = (): string => {
    if (!service || !organization) return t("contactForm.placeholders.message");
    if (ServiceRequiresAdditionalInformation(service))
      return t("contactForm.placeholders.message-required");
    if (ServiceIsWheelChange(service)) return t("contactFormMessages.wheeSizeDisclaimer");
    if (OrganizationIsRepareo(organization))
      return t("contactFormMessages.registrationDocumentDisclaimer");

    return t("contactForm.placeholders.message");
  };

  if (loading || !service || !garage) {
    return (
      <div className="grid place-items-center">
        <Loader />
      </div>
    );
  }

  const config = getConfig(organization);
  const showVinField = config.checkout.vin.display;
  const showClaimNumberField = config.checkout.claimNumber.display();
  const claimNumberRegex = config.checkout.claimNumber.regex;
  const claimNumberPattern = claimNumberRegex?.source || undefined;
  const claimNumberRequired = config.checkout.claimNumber.required();
  const showContractNumberField = config.checkout.contractNumber.display;
  const showReviewOptIn = config.checkout.optIns.reviewOptIn.show;
  const privacyPolicyUrl = config.legal.privacyPolicyUrl;
  const isExtraCostDay = !!(
    slot &&
    fleetVehicleReturn?.endDate &&
    config.dayPicker?.isExtraCostDay?.(
      new Date(parseISO(slot.starts_at)),
      fleetVehicleReturn.endDate,
    )
  );

  const submitForm = (e: FormEvent) => {
    e.preventDefault();
    if (numberPlate) {
      setNumberPlateChecking(true);
      checkNumberPlate().then((res) => {
        setNumberPlateChecking(false);
        res.data.numberPlateCheck.status ? submit(e) : setShowNumberPlateDialog(true);
      });
    } else {
      submit(e);
    }
  };

  const submitWithMissingNumberPlate = (e: FormEvent) => {
    setShowNumberPlateDialog(false);
    submit(e);
  };

  return (
    <form onSubmit={(e) => submitForm(e)}>
      {showNumberPlateDialog && (
        <Modal
          open={showNumberPlateDialog}
          onClose={() => setShowNumberPlateDialog(false)}
          title={t("contactForm.numberCheck.title")}
          width="max-w-3xl"
          showButton={false}
        >
          <div>
            {t("contactForm.numberCheck.text", {
              numberplate: numberPlate,
            })}
          </div>
          <div className="flex flex-col md:flex-row justify-between mt-10">
            <button
              type="button"
              onClick={() => setShowNumberPlateDialog(false)}
              className={`bg-warning w-full mb-6 md:mb-0 md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline focus:outline-none`}
            >
              {t("contactFormMessages.numberplateChange")}
            </button>
            <button
              type="button"
              onClick={(e) => submitWithMissingNumberPlate(e)}
              className={`bg-success w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline focus:outline-none`}
            >
              {t("contactFormMessages.numberplateCorrect")}
            </button>
          </div>
        </Modal>
      )}
      {error && (SlotAlreadyBooked(error) || SlotAlreadyBlocked(error)) && (
        <Fragment>
          <Modal
            open={true}
            title={t("contactFormMessages.slotAlreadyTakenTitle")}
            width="max-w-3xl"
            showButton={false}
          >
            <div>{t("contactForm.slot-out-of-stock")}</div>
          </Modal>
          <div className="errorWhenBlockingSlot rounded-md bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div>
                <div className="text-yellow-700">
                  <p>{t("contactForm.slot-out-of-stock")}</p>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {error && ActiveInquiryExistsForNumberplate(error) && (
        <Fragment>
          <Modal
            open={true}
            title={t("contactFormMessages.activeInquiryExistsForNumberplateErrorMessageTitle")}
            width="max-w-3xl"
            showButton={false}
          >
            <div>{t("contactForm.activeInquiryExistsForNumberplateErrorMessage")}</div>
          </Modal>
          <div className="rounded-md bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div>
                <div className="text-yellow-700">
                  <p>{t("contactForm.activeInquiryExistsForNumberplateErrorMessage")}</p>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {error && ActiveInquiryExistsForNumberplateAndService(error) && (
        <Fragment>
          <Modal
            open={true}
            title={t(
              "contactFormMessages.activeInquiryExistsForNumberplateAndServiceErrorMessageTitle",
            )}
            width="max-w-3xl"
            showButton={false}
          >
            <div>{t("contactForm.activeInquiryExistsForNumberplateAndServiceErrorMessage")}</div>
          </Modal>
          <div className="rounded-md bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div>
                <div className="text-yellow-700">
                  <p>{t("contactForm.activeInquiryExistsForNumberplateAndServiceErrorMessage")}</p>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {error && EarliestReturnDateNotReached(error) && (
        <Fragment>
          <Modal
            open={true}
            title={t("contactFormMessages.returnDateNotReachedTitle")}
            width="max-w-3xl"
            showButton={false}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t("contactFormMessages.returnDateNotReached", {
                  returnDate: formatDate(getEarliestReturnDate(error), "dd.MM.yyyy"),
                }),
              }}
            />
          </Modal>
          <div id={"returnDateNotReachedError"} className="rounded-md bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div>
                <div className="text-yellow-700">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t("contactFormMessages.returnDateNotReached", {
                        returnDate: formatDate(getEarliestReturnDate(error), "dd.MM.yyyy"),
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {error &&
        (NumberPlateNotFound(error) ||
          VehicleNotFoundInExternalFleet(error) ||
          NoDriverFoundForNumberPlate(error)) && (
          <Fragment>
            <Modal
              open={true}
              title={t("contactFormMessages.numberplateNotFoundTitle")}
              width="max-w-3xl"
              showButton={false}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: t("contactFormMessages.numberplateNotFoundMessage"),
                }}
              />
            </Modal>
            <div className="rounded-md bg-yellow-50 p-4 mb-6">
              <div className="flex">
                <div>
                  <div className="text-yellow-700">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t("contactFormMessages.numberplateNotFoundMessage"),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        )}
      {error &&
        (MeinAutoCustomerContractNotFound(error) ||
          MeinAutoNoTyreProductInCustomerContract(error)) && (
          <Fragment>
            <Modal
              open={true}
              title={t(
                "contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessageTitle",
              )}
              width="max-w-3xl"
              showButton={false}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    "contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessage",
                  ),
                }}
              />
            </Modal>
            <div className="rounded-md bg-yellow-50 p-4 mb-6">
              <div className="flex">
                <div>
                  <div className="text-yellow-700">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          "contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessage",
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        )}
      {error && CustomerAddressNotGiven(error) && (
        <Fragment>
          <Modal
            open={true}
            title={t("contactFormMessages.customerAddressNotGivenTitle")}
            width="max-w-3xl"
            showButton={false}
          ></Modal>
          <div id={"CustomerAddressNotGivenError"} className="rounded-md bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div>
                <div className="text-yellow-700">
                  <p>{t("contactFormMessages.customerAddressNotGiven")}</p>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      <Input
        id={"first-name"}
        name="first-name"
        className={"w-full"}
        placeholder={t("contactForm.placeholders.first-name")}
        value={firstName}
        onChange={setFirstName}
        validationMessage={
          validationErrors.firstName ? t("contactForm.validationErrors.first-name") : null
        }
        required={true}
      />
      <Input
        id={"last-name"}
        name="last-name"
        className={"w-full"}
        placeholder={t("contactForm.placeholders.last-name")}
        value={lastName}
        onChange={setLastName}
        validationMessage={
          validationErrors.lastName ? t("contactForm.validationErrors.last-name") : null
        }
        required={true}
      />
      <Input
        id={"email"}
        type={"email"}
        name="email"
        className={"w-full"}
        placeholder={t("contactForm.placeholders.email")}
        value={email}
        onChange={setEmail}
        validationMessage={validationErrors.email ? t("contactForm.validationErrors.email") : null}
        required={true}
      />
      <PhoneInput
        id={"phone"}
        type={"tel"}
        name="phone"
        className={"w-full"}
        placeholder={t("contactForm.placeholders.phone")}
        value={phone}
        onChange={setPhone}
        validationMessage={validationErrors.phone ? t("contactForm.validationErrors.phone") : null}
      />
      {(premiumServices.some((premiumService) =>
        PremiumServiceIsCollectAndReturn(premiumService),
      ) ||
        GarageHasNationwideService(garage)) && (
        <Field>
          <InputLabel>
            {GarageHasNationwideService(garage)
              ? t("contactFormMessages.nationwideServiceAddressHeadline")
              : t("contactForm.address-label")}
          </InputLabel>
          <AddressForm
            street={{
              value: street,
              onChange: setStreet,
              required: addressRequired,
              disabled: addressFormDisabled,
            }}
            city={{
              value: city,
              onChange: setCity,
              required: addressRequired,
              disabled: addressFormDisabled,
            }}
            postcode={{
              value: postcode,
              onChange: setPostcode,
              required: addressRequired,
              disabled: addressFormDisabled,
              validationMessage: /^\d*$/.test(postcode)
                ? undefined
                : t("contactForm.validationErrors.postcode"),
            }}
            addition={{ value: addition, onChange: setAddition, required: false }}
          />
        </Field>
      )}
      <Field>
        <InputLabel>{t("contactForm.number-plate-label")}</InputLabel>
        <NumberPlateInput
          value={numberPlate}
          countryCode={config.internationalNumberPlate ? countryCode : undefined}
          onChange={setNumberPlate}
          validationMessage={
            validationErrors.numberPlate ? t("contactForm.validationErrors.number-plate") : null
          }
          showNumberPlateSyncMessage={OrganizationIsArval(organization)}
        />
      </Field>
      {showVinField && (
        <div className={"space-y-2"}>
          <div className="font-bold mt-5">
            {t("contactForm.vinLabel")}
            {(config.checkout.vin.required ?? false) && "*"}
          </div>
          <VinInput
            vin={vin}
            setVin={setVIN}
            invalid={vinHint}
            setShowHint={setVinHint}
            required={config.checkout.vin.required ?? false}
          />
          {vinHint && (
            <div className={"border border-red-200 text-red-600 rounded-lg p-2 text-xs"}>
              <p>{t("contactForm.vinInvalidHint")}</p>
            </div>
          )}
          <div className={"border border-gray-200 rounded-lg p-2 text-xs"}>
            <p>{t("contactForm.vinHint")}</p>
          </div>
        </div>
      )}
      <Field>
        <InputLabel>{t("contactForm.mileage-label")}</InputLabel>
        <MileageInput
          value={mileage}
          onChange={setMileage}
          placeholder="30.000"
          className="w-full"
          validationMessage={
            validationErrors.mileage ? t("contactForm.validationErrors.mileage") : null
          }
        />
      </Field>
      {showClaimNumberField && (
        <Field>
          <InputLabel>
            {t(`contactForm.claimNumber.label.${claimNumberRequired ? "required" : "default"}`)}
          </InputLabel>
          <Input
            id={"claimNumber"}
            type={"claimNumber"}
            name="claimNumber"
            className={"w-full"}
            placeholder={t("contactForm.claimNumber.placeholder.default")}
            value={claimNumber ?? ""}
            onChange={setClaimNumber}
            pattern={claimNumberPattern}
            required={claimNumberRequired}
          />
          <InputHint>
            {t(`contactForm.claimNumber.hint.${claimNumberRequired ? "required" : "default"}`)}
          </InputHint>
        </Field>
      )}
      {showContractNumberField && (
        <Field>
          <InputLabel>{t("contactForm.contractNumber.label")}</InputLabel>
          <Input
            id={"contractNumber"}
            type={"contractNumber"}
            name="contractNumber"
            className={"w-full"}
            placeholder={t("contactForm.contractNumber.placeholder")}
            value={contractNumber ?? ""}
            onChange={setContractNumber}
          />
        </Field>
      )}
      {OrganizationIsALD(organization) &&
        premiumServices.some((premiumService) => PremiumServiceIsRentalCar(premiumService)) && (
          <Field>
            <InputLabel>
              {t("molecules.whitelabel.ayvens.rentalCarSelect.categories.label")}
            </InputLabel>
            <RentalCarSelect />
          </Field>
        )}

      <div className="font-bold mt-5">
        {service.requires_additional_information && !OrganizationIsALD(organization)
          ? t("contactForm.message-label-required", {
              garageType: garage?.garageType?.display_name_singular,
            })
          : t("contactForm.message-label", {
              garageType: garage?.garageType?.display_name_singular,
            })}
      </div>

      <MessageInput message={message} onChange={setMessage} placeholder={getPlaceholder()} />
      {validationErrors.message && (
        <p className="mt-1 mb-4 text-sm text-error-600">
          {t("contactForm.validationErrors.message")}
        </p>
      )}
      {OrganizationIsMFS(organization) && (
        <div className="flex mt-3">
          <Checkbox
            id={"additionalCosts"}
            status={additionalCosts}
            onChange={setAdditionalCosts}
            required={true}
          />
          <label htmlFor={"additionalCosts"}>
            Ich bestätige, dass bei einer Stonierung des Termins weniger als 72 Stunden vor Beginn
            Kosten für mich entstehen können.
          </label>
        </div>
      )}
      {(OrganizationIsMeinAuto(organization) || OrganizationIsMARA(organization)) &&
        isExtraCostDay && (
          <div className="flex mt-3">
            <Checkbox
              id={"additionalCosts"}
              status={additionalCosts}
              onChange={setAdditionalCosts}
              required={true}
            />
            <label htmlFor={"additionalCosts"}>
              Ich habe die{" "}
              <a href={"#extra-cost-tos"}>
                Hinweise zur vorzeitigen Beendigung des Leasingvertrages
              </a>{" "}
              gelesen und akzeptiere, dass Zusatzkosten für mich entstehen.
            </label>
          </div>
        )}
      <div className="flex mt-3">
        <Checkbox id={"legal"} status={tos} onChange={setTos} required={true} />
        {OrganizationIsALD(organization) ? (
          <label htmlFor="legal">
            Ich stimme den{" "}
            <a href={termsLink} target="_blank" rel="noopener">
              Nutzungsbedingungen
            </a>{" "}
            zu und habe die Datenschutzhinweise von{" "}
            <a href={ayvensPrivacyLink} target="_blank" rel="noopener">
              Ayvens
            </a>{" "}
            und{" "}
            <a href={privacyPolicyUrl} target="_blank" rel="noopener">
              repareo
            </a>{" "}
            zur Kenntnis genommen. Meine Kontaktdaten dürfen für Rückfragen zu den Terminen genutzt
            werden.*
          </label>
        ) : (
          <label htmlFor="legal">
            Ich stimme den{" "}
            <a href={termsLink} target="_blank" rel="noopener">
              Nutzungsbedingungen
            </a>{" "}
            und der{" "}
            <a href={privacyPolicyUrl} target="_blank" rel="noopener">
              Datenschutzerklärung
            </a>{" "}
            zu. Meine Kontaktdaten dürfen für Rückfragen zu den Terminen genutzt werden.* Widerruf
            jederzeit möglich.
          </label>
        )}
      </div>
      {validationErrors.tos && (
        <div className={"mb-3 text-sm text-error"}>{t("contactForm.validationErrors.tos")}</div>
      )}

      {showReviewOptIn && (
        <div className="flex mt-3">
          <Checkbox id={"reviewOptIn"} status={reviewOptIn} onChange={setReviewOptIn} />
          <label htmlFor={"reviewOptIn"}>{t("contactForm.reviewOptIn.label")}</label>
        </div>
      )}

      {OrganizationIsRepareo(organization) && (
        <Fragment>
          <div className="flex mt-3">
            <Checkbox id={"marketing"} status={marketing} onChange={setMarketing} />
            <label htmlFor={"marketing"}>{t("contactForm.marketing")}</label>
          </div>
          {!ServiceIsCertificate(service) && (
            <div className="flex mt-3 items-center relative">
              <Checkbox id={"adacMember"} status={adacMember} onChange={setAdacMember} />
              <label htmlFor={"adacMember"} className={"mb-2"}>
                Ich bin ADAC Mitglied und möchte an der{" "}
                <span
                  className={"border-b border-dashed cursor-pointer"}
                  onMouseOver={() => setTooltipShowing(true)}
                  onMouseLeave={() => setTooltipShowing(false)}
                >
                  ADAC 3 % Cashback-Action
                </span>{" "}
                teilnehmen.
              </label>
              {tooltipShowing && (
                <div>
                  <p
                    className={
                      "absolute text-sm rounded-md w-[300px] right-0 md:left-80 top-10 p-2 bg-white shadow z-20"
                    }
                  >
                    {t("contactForm.adac-tip")}
                  </p>
                </div>
              )}
            </div>
          )}
        </Fragment>
      )}

      <button
        type="submit"
        disabled={numberPlateChecking}
        className={`w-full bg-primary px-8 py-2 mt-5 sm:w-72 text-white rounded text-center font-bold font-serif cursor-pointer ${numberPlateChecking ? "bg-primary-200" : ""}`}
      >
        {numberPlateChecking
          ? t("contactForm.submitting")
          : service && ServiceIsVehicleReturn(service)
            ? isExtraCostDay
              ? `Kostenpflichtig ${t("contactForm.vehicleReturn.submit")}`
              : t("contactForm.vehicleReturn.submit")
            : t("contactForm.submit")}
      </button>

      {(OrganizationIsMobilityConcept(organization) || OrganizationIsMeinAuto(organization)) && (
        <div
          className={"text-sm italic font-bold mt-3 text-primary"}
          dangerouslySetInnerHTML={{
            __html: t("generalWords.hint") + "" + t("booking.hint"),
          }}
        ></div>
      )}
      <div className={"text-sm italic mt-3"}>{t("contactForm.mandatory-field-notice")}</div>

      {OrganizationIsALD(organization) && (
        <div className={"text-xs mt-3 text-gray-500"}>
          <span className={"font-semibold"}>{t("contactForm.notice-bold")}</span>{" "}
          {t("contactForm.notice-normal")}
        </div>
      )}
      {(OrganizationIsMeinAuto(organization) || OrganizationIsMARA(organization)) &&
        ServiceIsVehicleReturn(service) &&
        isExtraCostDay && <VehicleReturnTOS />}
    </form>
  );
};
